import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { accountAPI, handleApiError } from '../../utils/api';

/**
 * Page de changement de mot de passe. Utilisée aussi bien :
 * - volontairement par un utilisateur connecté (lien "Changer mon mot de
 *   passe" dans son menu)
 * - de force, juste après une connexion avec un mot de passe temporaire
 *   généré par un admin (voir ProtectedRoute dans App.js, qui redirige
 *   ici tant que mustChangePassword est true)
 */
const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const { mustChangePassword, clearMustChangePassword } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);
    try {
      await accountAPI.changePassword(currentPassword, newPassword);
      clearMustChangePassword();
      setSuccess(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
        <div className="text-center mb-6">
          <ShieldCheck className="mx-auto text-blue-600 mb-2" size={40} />
          <h1 className="text-2xl font-bold text-gray-800">Changer mon mot de passe</h1>
          {mustChangePassword && (
            <p className="text-gray-600 mt-2 text-sm">
              Un administrateur a réinitialisé votre mot de passe. Choisissez-en un nouveau pour continuer.
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4 text-center">
            Mot de passe changé avec succès. Redirection...
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe {mustChangePassword ? 'temporaire' : 'actuel'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isLoading ? 'Enregistrement...' : 'Changer le mot de passe'}
            </button>

            {!mustChangePassword && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full text-gray-600 py-2 text-sm hover:text-gray-800"
              >
                Annuler
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordPage;
